<script setup lang="ts">
/**
 * "Notify N households of this change?" (SPEC §6.24.1 — MT-163)
 *
 * Asked after a change has been saved, never before and never on its own. The
 * organizer sees who it would reach, who it would not, and what it would say,
 * and then either sends it or does not. Five saves in a row are five chances
 * to say no.
 *
 * A cancellation always asks, with sending as the primary action: a family
 * that turns up to a cancelled meal is the worst outcome this product has.
 */
import { computed, onMounted, ref } from 'vue'
import { ApiError, endpoints, type ChangeNoticePreviewResponse } from '@/api'
import { asCount } from '@/utils/numbers'

const props = defineProps<{
  groupId: string
  eventId: string
  kind: 'Changed' | 'Cancelled'
  /** For a change: which of time, place, meal. */
  changes?: string[]
}>()

const emit = defineEmits<{ done: [outcome: 'sent' | 'skipped'] }>()

const preview = ref<ChangeNoticePreviewResponse | null>(null)
const loading = ref(true)
const sending = ref(false)
const sent = ref<number | null>(null)
const error = ref<string | null>(null)

const people = computed(() => asCount(preview.value?.people))
const households = computed(() => asCount(preview.value?.households))
const unreached = computed(() => asCount(preview.value?.householdsNotReached))

function problem(caught: unknown, fallback: string): string {
  return (
    (caught instanceof ApiError &&
      (Object.values(caught.problem?.errors ?? {})[0]?.[0] ?? caught.problem?.title)) ||
    fallback
  )
}

onMounted(async () => {
  try {
    preview.value = await endpoints.previewChangeNotice(props.groupId, props.eventId, {
      kind: props.kind,
      changes: props.changes ?? [],
    })
  } catch (caught) {
    error.value = problem(caught, 'We could not work out who to tell just then.')
  } finally {
    loading.value = false
  }
})

async function send(): Promise<void> {
  sending.value = true
  error.value = null

  try {
    const result = await endpoints.queueChangeNotice(props.groupId, props.eventId, {
      kind: props.kind,
      changes: props.changes ?? [],
    })
    sent.value = asCount(result.people)
    emit('done', 'sent')
  } catch (caught) {
    error.value = problem(caught, 'That did not queue. Try again in a moment.')
  } finally {
    sending.value = false
  }
}
</script>

<template>
  <section class="notice" data-testid="change-notice">
    <h2 class="notice__title">
      {{ kind === 'Cancelled' ? 'Tell the families it is off?' : 'Tell the families it changed?' }}
    </h2>

    <v-skeleton-loader v-if="loading" type="paragraph" />

    <template v-else-if="preview">
      <p class="notice__reach" data-testid="change-notice-reach">
        {{ households }} {{ households === 1 ? 'household is' : 'households are' }} invited.
        <template v-if="people > 0">
          Email reaches {{ people }} {{ people === 1 ? 'person' : 'people'
          }}<template v-if="unreached > 0"
            >; {{ unreached }} {{ unreached === 1 ? 'household' : 'households' }} can't be emailed
            and will need telling another way</template
          >.
        </template>
        <template v-else>Nobody invited has a confirmed email address.</template>
      </p>

      <pre class="notice__preview" data-testid="change-notice-preview">{{ preview.preview }}</pre>

      <p v-if="sent !== null" class="notice__sent" role="status" data-testid="change-notice-sent">
        Queued for {{ sent }} {{ sent === 1 ? 'person' : 'people' }}. It will go out in the next few
        minutes.
      </p>

      <div v-else class="notice__actions">
        <v-btn
          v-if="people > 0"
          color="primary"
          variant="flat"
          :loading="sending"
          data-testid="change-notice-send"
          @click="send"
        >
          Email {{ people }} {{ people === 1 ? 'person' : 'people' }}
        </v-btn>
        <v-btn variant="text" data-testid="change-notice-skip" @click="emit('done', 'skipped')">
          {{ people > 0 ? "Don't notify" : 'Close' }}
        </v-btn>
      </div>
    </template>

    <p v-if="error" class="notice__error" role="alert">{{ error }}</p>
  </section>
</template>

<style scoped>
.notice {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.notice__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
}

.notice__reach {
  margin: 0;
}

.notice__preview {
  margin: 0;
  padding: 12px;
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 0.875rem;
  border-radius: 8px;
  background: rgb(var(--v-theme-surface-variant));
  color: rgb(var(--v-theme-on-surface-variant));
  max-height: 16rem;
  overflow: auto;
}

.notice__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.notice__sent {
  margin: 0;
  color: rgb(var(--v-theme-success));
  font-weight: 500;
}

.notice__error {
  margin: 0;
  color: rgb(var(--v-theme-error));
}
</style>
